/** /tag_canonicals/ — cross-tenant Tag_Canonical catalog (list + CRUD).
 *
 * A canonical is the token `Tag_Name_Alias` normalises names ONTO, and this is
 * where its own attributes live. A tag's EFFECTIVE attributes are its own
 * `data` over its canonical's, per key, the tag winning — so a reseller policy
 * written once covers every spelling of that reseller, including one ingestion
 * mints next week. Production carries 4,287 distinct reseller names and they
 * splinter continuously; that leak is what this closes.
 *
 * `tour_operator` is nullable: null = system-wide, and a tenant row shadows the
 * system row with the same `(category_code, token)` — including shadowing it to
 * `enabled: false`, which is how a tenant declines a system canonical instead
 * of inheriting it.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). No optimistic-concurrency token — writes are
 * last-writer-wins.
 */
import { runBatch, type BatchResponse, type BatchSelection } from './batch';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

export interface TagCanonical {
  id: number;
  /** Owning tenant. Null for a system-wide canonical. */
  tenant: TenantRef | null;
  /** Tag category this canonical belongs to, e.g. 'reseller'. Stored lowercased. */
  category_code: string;
  /** The governed token, e.g. 'viator'. Stored lowercased. */
  token: string;
  enabled: boolean;
  /**
   * How many aliases normalise onto this token IN THE SAME SCOPE. A system
   * canonical does not count tenants' private remaps, or the number would swell
   * until it meant nothing.
   */
  alias_count: number;
  created_ts: string | null;
  updated_ts: string | null;
}

export interface TagCanonicalDetail extends TagCanonical {
  /** The canonical's own attributes; a tag's own data wins key by key. */
  data: Record<string, unknown> | null;
  /**
   * Advisory validation of `data` against the category's schema — the SAME
   * schema that governs the category's tags. Never blocking: the write goes
   * through and these are shown.
   */
  schema_problems: string[];
}

/** Create/update body. `category_code` + `token` required on create;
 *  `tour_operator` null = system-wide. */
export interface TagCanonicalWrite {
  tour_operator?: number | null;
  category_code?: string;
  token?: string;
  data?: Record<string, unknown> | null;
  enabled?: boolean;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  /** token + category_code (icontains). */
  search?: string;
  /** One id or a comma list of ids (D1), or 'system' / 'null' for system-wide canonicals (no tenant). */
  tour_operator_id?: TenantIdsParam;
  category_code?: string;
  enabled?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) {
    usp.set('tour_operator_id', String(params.tour_operator_id));
  }
  if (params.category_code) usp.set('category_code', params.category_code);
  if (params.enabled !== undefined) usp.set('enabled', String(params.enabled));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<TagCanonical>> {
  return apiGet<PaginatedResponse<TagCanonical>>(`/tag_canonicals/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<TagCanonicalDetail> {
  return apiGet<TagCanonicalDetail>(`/tag_canonicals/${id}/`);
}

export function create(body: TagCanonicalWrite): Promise<TagCanonicalDetail> {
  return apiPost<TagCanonicalDetail>('/tag_canonicals/', body);
}

export function update(id: number, body: TagCanonicalWrite): Promise<TagCanonicalDetail> {
  return apiPatch<TagCanonicalDetail>(`/tag_canonicals/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/tag_canonicals/${id}/`);
}

/** Generic `/tag_canonicals/batch/` delete dispatch (background task). */
export function batchDelete(
  selection: BatchSelection,
  filterQuery?: string,
): Promise<BatchResponse> {
  return runBatch('tag_canonicals', 'delete', selection, undefined, filterQuery);
}
